import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';

function ProfileHeader({ profile, isOwnProfile, friendButton }) {
  const {
    username,
    displayName,
    bio,
    avatar,
    stats,
    streak,
    createdAt,
  } = profile;

  // Get initials for avatar fallback
  const initials = displayName
    ? displayName.split(' ').map((n) => n[0]).join('')
      .toUpperCase()
      .slice(0, 2)
    : username?.slice(0, 2).toUpperCase() || '??';

  // Format "Member Since" date
  const memberSince = createdAt
    ? new Date(createdAt).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
    : '';

  return (
    <div className="px-4 py-8">
      {/* Desktop Layout */}
      <div className="hidden md:flex gap-8 max-w-4xl mx-auto">
        {/* Avatar */}
        <div className="flex-shrink-0">
          <div className="w-[150px] h-[150px] rounded-full bg-gray-200 dark:bg-gray-700 overflow-hidden flex items-center justify-center">
            {avatar ? (
              <img
                src={avatar}
                alt={username}
                className="w-full h-full object-cover"
              />
            ) : (
              <span className="text-4xl font-semibold text-gray-500 dark:text-gray-400">
                {initials}
              </span>
            )}
          </div>
        </div>

        {/* Info */}
        <div className="flex-1">
          {/* Username row */}
          <div className="flex items-center gap-4 mb-4">
            <h1 className="text-xl text-puzzle-text">{username}</h1>
            {isOwnProfile ? (
              <Link
                to="/account"
                className="px-4 py-1.5 bg-puzzle-background border border-puzzle-border rounded-lg text-sm font-semibold text-puzzle-text hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                Edit profile
              </Link>
            ) : friendButton}
          </div>

          {/* Stats */}
          <div className="flex gap-8 mb-4">
            <div className="text-puzzle-text">
              <span className="font-semibold">{stats?.totalCompletions || 0}</span>
              {' '}
              puzzles
            </div>
            <div className="text-puzzle-text">
              <span className="font-semibold">{streak?.current || 0}</span>
              {' '}
              day streak
            </div>
            <div className="text-puzzle-text-secondary text-sm">
              Member since
              {' '}
              {memberSince}
            </div>
          </div>

          {/* Display name and bio */}
          <div>
            {displayName && <p className="font-semibold text-puzzle-text">{displayName}</p>}
            {bio && <p className="text-puzzle-text mt-1">{bio}</p>}
          </div>
        </div>
      </div>

      {/* Mobile Layout */}
      <div className="md:hidden">
        <div className="flex items-center gap-4 mb-4">
          {/* Avatar */}
          <div className="w-[77px] h-[77px] rounded-full bg-gray-200 dark:bg-gray-700 overflow-hidden flex items-center justify-center flex-shrink-0">
            {avatar ? (
              <img
                src={avatar}
                alt={username}
                className="w-full h-full object-cover"
              />
            ) : (
              <span className="text-2xl font-semibold text-gray-500 dark:text-gray-400">
                {initials}
              </span>
            )}
          </div>

          {/* Stats (mobile) */}
          <div className="flex-1 grid grid-cols-3 text-center">
            <div>
              <p className="font-semibold text-puzzle-text">{stats?.totalCompletions || 0}</p>
              <p className="text-xs text-puzzle-text-secondary">puzzles</p>
            </div>
            <div>
              <p className="font-semibold text-puzzle-text">{streak?.current || 0}</p>
              <p className="text-xs text-puzzle-text-secondary">day streak</p>
            </div>
            <div>
              <p className="font-semibold text-puzzle-text text-sm">{memberSince}</p>
              <p className="text-xs text-puzzle-text-secondary">joined</p>
            </div>
          </div>
        </div>

        {/* Name and bio */}
        <div className="mb-4">
          {displayName && <p className="font-semibold text-puzzle-text">{displayName}</p>}
          {bio && <p className="text-sm text-puzzle-text mt-1">{bio}</p>}
        </div>

        {/* Action button (mobile) */}
        {isOwnProfile ? (
          <Link
            to="/account"
            className="block w-full py-1.5 bg-puzzle-background border border-puzzle-border rounded-lg text-sm font-semibold text-puzzle-text text-center"
          >
            Edit profile
          </Link>
        ) : friendButton}
      </div>
    </div>
  );
}

ProfileHeader.propTypes = {
  profile: PropTypes.shape({
    username: PropTypes.string.isRequired,
    displayName: PropTypes.string,
    bio: PropTypes.string,
    avatar: PropTypes.string,
    createdAt: PropTypes.string,
    stats: PropTypes.shape({
      totalCompletions: PropTypes.number,
    }),
    streak: PropTypes.shape({
      current: PropTypes.number,
    }),
  }).isRequired,
  isOwnProfile: PropTypes.bool,
  friendButton: PropTypes.node,
};

ProfileHeader.defaultProps = {
  isOwnProfile: false,
  friendButton: null,
};

export default ProfileHeader;
